import { faPencil, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { format, parse } from 'date-fns';
import { useFormikContext } from 'formik';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Card, CardContent } from 'src/components/Card';
import { ColorPicker } from 'src/components/ColorPicker';
import { DatePicker } from 'src/components/DatePicker';
import { FormLabel } from 'src/components/FormLabel';
import { Grid } from 'src/components/Grid';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Link } from 'src/components/Link';
import { Popover } from 'src/components/Popover';
import { SectionHeader } from 'src/components/SectionHeader';
import { TimePicker } from 'src/components/TimePicker';
import { Typography } from 'src/components/Typography';
import { debounce } from 'src/components/muiTheme/utils';
import { externalLinks } from 'src/externalLinks';
import { getUserDisplayName } from 'src/models/user/utils';
import { TooltipLabel } from 'src/pages/Exercises/NewExercise/TooltipLabel';
import { FormCheckbox } from 'src/shared/Forms/Fields/FormCheckbox';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';
import { BaseSelect } from 'src/shared/Forms/Selects/BaseSelect';
import { usePermissionCheck } from 'src/shared/usePermissionCheck';
import { Identifier } from 'src/types/utilityTypes';
import { formatMonthDayYear } from 'src/utils/dateFormatter';
import { generateAvailableTimes } from 'src/utils/utils';

import { RecurringOptions } from './RecurringOptions';
import { useManageSchedule } from './useManageSchedule';

export type FormValues = {
  name: string;
  scheduleType: string;
  noTrainers: boolean;
  startDate: string;
  endDate: string;
  recurringCount: number;
  recurringType: string;
  days: string[];
  recurringDayNumbers: number[];
  startTime: string;
  endTime: string;
  trainerId: Identifier;
  subTrainerId: Identifier;
  locationId: Identifier;
  serviceIds: Identifier[];
  blockAvailable: boolean;
  intervalInMinutes: number | null;
  bufferBeforeInMinutes: number | string;
  bufferInMinutes: number | string;
  color: string;
};

type ManageSchedulesProps = {
  unavailableOnly?: boolean;
};

const types = [
  { name: 'Available', id: 'available' },
  { name: 'Unavailable', id: 'unavailable' },
];

const gridSize = { md: 6, xs: 12 };
const gridSize2 = { md: 4, xs: 12 };
const gridStyle = { display: 'flex', flexDirection: 'column', gap: 1 };

export const ManageSchedule = ({
  unavailableOnly = false,
}: ManageSchedulesProps) => {
  const { t: tTrainer } = useTranslation('translation', {
    keyPrefix: 'trainer',
  });

  const { t: tLocation } = useTranslation('translation', {
    keyPrefix: 'location',
  });

  const { t } = useTranslation('translation', {
    keyPrefix: 'schedules.manageSchedules',
  });

  const { values, setFieldValue, errors } = useFormikContext<FormValues>();
  const { canEditSchedules, canEditOthersSchedules } = usePermissionCheck();

  const [advanced, setAdvanced] = useState(false);
  const [color, setColor] = useState(values.color || '');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);

  const open = Boolean(anchorEl);

  const canEditStaff = canEditSchedules && canEditOthersSchedules;

  const { trainers, locations, services } = useManageSchedule();

  const trainerOptions = useMemo(() => {
    const options =
      trainers?.map((trainer) => ({
        value: trainer.id as string,
        label: getUserDisplayName(trainer) || '',
      })) || [];

    options.unshift({
      value: '',
      label:
        values.scheduleType === 'available'
          ? t('selectSubTrainer')
          : t('allSubTrainers'),
    });

    return options;
  }, [t, trainers, values.scheduleType]);

  const locationOptions = useMemo(() => {
    const options =
      locations?.map((location) => ({
        value: location.id as string,
        label: location.name,
      })) || [];

    options.unshift({
      value: '',
      label: t('anyLocation'),
    });
    return options;
  }, [locations, t]);

  const serviceOptions = useMemo(() => {
    const options = (services?.map((service) => ({
      id: service.id,
      value: service,
      label: service.name || '',
    })) || []) as any[];

    options.unshift({
      id: '',
      value: null,
      label: t('addNewService'),
    });
    return options;
  }, [services, t]);

  const location = useMemo(() => {
    return (
      locations?.find((location) => location.id === values.locationId) || null
    );
  }, [locations, values.locationId]);

  const subTrainer = useMemo(() => {
    return (
      trainers?.find((trainer) => trainer.id === values.subTrainerId) || null
    );
  }, [trainers, values.subTrainerId]);

  const timeZone = useMemo(() => {
    return location?.timeZone || subTrainer?.timeZone || '';
  }, [location, subTrainer]);

  const customInterval = useMemo(() => {
    return values.intervalInMinutes !== null;
  }, [values.intervalInMinutes]);

  const interval = useMemo(() => {
    const i = values.intervalInMinutes;
    return i === null ? 0 : i;
  }, [values.intervalInMinutes]);

  const debouncedSetColor = debounce((value: string) => {
    setFieldValue('color', value);
  }, 300);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Card>
        <CardContent>
          <Grid container spacing={3}>
            <Grid size={gridSize}>
              <FormSelect
                name="scheduleType"
                label={t('scheduleTypeLabel')}
                options={types.map((type) => ({
                  value: type.id,
                  label: type.name,
                }))}
                required
                onChange={(e) => {
                  setFieldValue('scheduleType', e.target.value);
                  if (e.target.value === 'unavailable') {
                    setFieldValue('noTrainers', false);
                  }
                }}
                size="medium"
                disabled={unavailableOnly}
              />
            </Grid>

            {values.scheduleType === 'unavailable' && (
              <Grid size={gridSize}>
                <FormTextField
                  name="name"
                  label={t('nameLabel')}
                  placeholder={t('namePlaceholder')}
                />
              </Grid>
            )}

            {unavailableOnly ? (
              <Grid size={gridSize}>
                <Typography variant="body2">
                  {t('dateLabel', {
                    date: formatMonthDayYear(values.startDate),
                  })}
                </Typography>
              </Grid>
            ) : (
              <>
                <Grid size={gridSize}>
                  <DatePicker
                    name="startDate"
                    label={t('startDateLabel')}
                    disabled={unavailableOnly}
                    required
                    value={
                      values.startDate
                        ? parse(values.startDate, 'yyyy-MM-dd', new Date())
                        : null
                    }
                    onChange={(value) => {
                      setFieldValue(
                        'startDate',
                        value ? format(value, 'yyyy-MM-dd') : ''
                      );
                    }}
                    error={!!errors.startDate}
                    helperText={errors.startDate}
                  />
                </Grid>
                <Grid size={gridSize}>
                  {values.endDate ? (
                    <DatePicker
                      name="endDate"
                      label={t('endDateLabel')}
                      disabled={unavailableOnly}
                      value={
                        values.endDate
                          ? parse(values.endDate, 'yyyy-MM-dd', new Date())
                          : null
                      }
                      onChange={(value) => {
                        setFieldValue(
                          'endDate',
                          value ? format(value, 'yyyy-MM-dd') : ''
                        );
                      }}
                      error={!!errors.endDate}
                      helperText={errors.endDate}
                    />
                  ) : (
                    <FormTextField
                      name="endDate"
                      label={t('endDateLabel')}
                      placeholder={t('none')}
                      disabled
                    />
                  )}
                  <Link
                    component="button"
                    type="button"
                    onClick={() => {
                      if (values.endDate) {
                        setFieldValue('endDate', null);
                      } else {
                        setFieldValue(
                          'endDate',
                          format(new Date(), 'yyyy-MM-dd')
                        );
                      }
                    }}
                  >
                    {values.endDate ? t('removeEndDate') : t('addEndDate')}
                  </Link>
                </Grid>

                <RecurringOptions />

                <Grid size={gridSize}>
                  <TimePicker
                    name="startTime"
                    label={
                      <>
                        {t('startTimeLabel')} {timeZone && `(${timeZone})`} *
                      </>
                    }
                    timeSteps={{
                      minutes: 15,
                    }}
                    value={
                      values.startTime
                        ? new Date(`1970-01-01 ${values.startTime}`)
                        : null
                    }
                    onChange={(value) => {
                      setFieldValue(
                        'startTime',
                        value ? format(value, 'HH:mm') : ''
                      );
                    }}
                    error={!!errors.startTime}
                    helperText={errors.startTime}
                  />
                </Grid>

                <Grid size={gridSize}>
                  <TimePicker
                    name="endTime"
                    label={
                      <>
                        {t('endTimeLabel')} {timeZone && `(${timeZone})`} *
                      </>
                    }
                    timeSteps={{
                      minutes: 15,
                    }}
                    value={
                      values.endTime
                        ? new Date(`1970-01-01 ${values.endTime}`)
                        : null
                    }
                    onChange={(value) => {
                      setFieldValue(
                        'endTime',
                        value ? format(value, 'HH:mm') : ''
                      );
                    }}
                    error={!!errors.endTime}
                    helperText={errors.endTime}
                  />
                </Grid>

                <Grid size={gridSize} sx={gridStyle}>
                  <FormLabel>{tTrainer('one')}</FormLabel>
                  {!values.noTrainers && (
                    <Box>
                      {values.scheduleType === 'available' ? (
                        <HtmlTypography variant="body2">
                          {`${t('sessionsIncludeDefaultTrainer', {
                            trainer: tTrainer('other'),
                          })}`}
                        </HtmlTypography>
                      ) : !values.subTrainerId ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                          <HtmlTypography sx={{ mr: '0.25rem' }}>
                            {`${t('hideAllSessions', {
                              trainer: tTrainer('other'),
                            })}`}
                          </HtmlTypography>
                          <Link href={externalLinks.booking()} target="_blank">
                            {t('bookingPage')}.
                          </Link>
                        </Box>
                      ) : (
                        <Box sx={{ height: '1.5rem' }}></Box>
                      )}

                      {!canEditStaff && (
                        <HtmlTypography variant="body2">
                          {`${t('noPermission', {
                            trainer: tTrainer('other').toLowerCase(),
                          })}`}
                        </HtmlTypography>
                      )}

                      <FormSelect
                        name="subTrainerId"
                        disabled={!canEditStaff}
                        options={trainerOptions}
                        size="medium"
                      />
                    </Box>
                  )}

                  {values.scheduleType === 'available' && (
                    <FormCheckbox
                      name="noTrainers"
                      label={t('disableTrainer', {
                        trainer: tTrainer('other'),
                      })}
                      disabled={!canEditStaff}
                    />
                  )}
                </Grid>

                <Grid size={gridSize} sx={gridStyle}>
                  <FormLabel>{tLocation('upper')}</FormLabel>
                  <Box>
                    <HtmlTypography variant="body2">
                      {t('locationSubLabel', {
                        trainer: tTrainer('one'),
                      })}
                    </HtmlTypography>
                    <FormSelect
                      name="locationId"
                      options={locationOptions}
                      size="medium"
                    />
                  </Box>
                </Grid>
              </>
            )}
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          {values.scheduleType === 'available' ? (
            <Grid size={gridSize}>
              <FormLabel>{t('bookableServices')}</FormLabel>
              <HtmlTypography variant="body2">
                {t('bookableServicesSubLabel', {
                  trainer: tTrainer('client.other').toLowerCase(),
                })}
              </HtmlTypography>
              <BaseSelect
                options={serviceOptions}
                value={''}
                valueOnly={false}
                blurOnSelect={true}
                onChange={(v) => {
                  if (!v?.id) return;
                  const s = values.serviceIds.find((s) => s === v.id);
                  if (!s) {
                    const res = values.serviceIds.concat(v.id);
                    setFieldValue('serviceIds', res);
                  }
                }}
              />
              <Box
                sx={{
                  backgroundColor: 'background.level2',
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  display: values.serviceIds.length > 0 ? 'block' : 'none',
                }}
              >
                {values.serviceIds?.map((serviceId) => (
                  <Box
                    key={serviceId}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography>
                        {services?.find((s) => s.id === serviceId)?.name}
                      </Typography>
                      <IconButton
                        color="primary"
                        size="small"
                        LinkComponent={Link}
                        href={externalLinks.servicesEdit(serviceId)}
                        target="_blank"
                      >
                        <Icon
                          icon={faPencil}
                          size="small"
                        />
                      </IconButton>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => {
                          setFieldValue(
                            'serviceIds',
                            values.serviceIds.filter((s) => s !== serviceId)
                          );
                        }}
                      >
                        <Icon
                          icon={faTrash}
                          size="small"
                        />
                      </IconButton>
                    </Box>
                    <Link
                      component="button"
                      type="button"
                      onClick={(e) => {
                        setAnchorEl(e.currentTarget);
                        const availableTimes = generateAvailableTimes({
                          start: values.startTime,
                          end: values.endTime,
                          defaultTime:
                            services?.find((s) => s.id === serviceId)
                              ?.defaultTime || 0,
                          intervalInMinutes: interval,
                        });
                        setAvailableTimes(availableTimes);
                      }}
                    >
                      {t('seeAvailableTimes')}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Grid>
          ) : (
            <Grid container spacing={3}>
              <Grid size={gridSize}>
                <FormCheckbox
                  name="blockAvailable"
                  label={t('blockAvailable')}
                />
                <HtmlTypography variant="body2">
                  {t('blockAvailableSubLabel', {
                    trainer: tTrainer('one').toLowerCase(),
                  })}
                </HtmlTypography>
              </Grid>
            </Grid>
          )}
        </CardContent>
      </Card>
      {values.scheduleType === 'available' && (
        <Card>
          <SectionHeader title={t('advancedSettings')} bottomDivider />
          <CardContent>
            <Link
              component="button"
              type="button"
              onClick={() => {
                setFieldValue('intervalInMinutes', customInterval ? null : 0);
              }}
              sx={{ mb: 2 }}
            >
              {customInterval
                ? t('useServiceTimeAsInterval')
                : t('useCustomInterval')}
            </Link>
            <Grid container spacing={3}>
              {customInterval && (
                <Grid size={customInterval ? gridSize2 : gridSize}>
                  <TooltipLabel
                    text={t('customBookingInterval')}
                    tooltip={t('customBookingIntervalTooltip')}
                  />
                  <FormTextField
                    name="intervalInMinutes"
                    type="number"
                    placeholder={t('enterMinutes')}
                    InputProps={{
                      inputProps: { min: 0 },
                    }}
                  />
                </Grid>
              )}
              <Grid size={customInterval ? gridSize2 : gridSize}>
                <TooltipLabel
                  text={t('timeBufferBeforeSessionInMinutes')}
                  tooltip={t('timeBufferBeforeSessionInMinutesTooltip')}
                />
                <FormTextField
                  name="bufferBeforeInMinutes"
                  type="number"
                  placeholder={t('enterMinutes')}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
              <Grid size={customInterval ? gridSize2 : gridSize}>
                <TooltipLabel
                  text={t('timeBufferAfterSessionInMinutes')}
                  tooltip={t('timeBufferAfterSessionInMinutesTooltip')}
                />
                <FormTextField
                  name="bufferInMinutes"
                  type="number"
                  placeholder={t('enterMinutes')}
                  InputProps={{
                    inputProps: { min: 0 },
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {!unavailableOnly && (
            <>
              {!unavailableOnly && (
                <Link
                  component="button"
                  type="button"
                  onClick={() => setAdvanced(!advanced)}
                >
                  {advanced
                    ? t('hideAdvancedSettings')
                    : t('showAdvancedSettings')}
                </Link>
              )}
            </>
          )}

          {(advanced || unavailableOnly) && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 1,
                alignItems: 'flex-end',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t('scheduleColor')}:
              </Typography>
              <ColorPicker
                label=""
                color={color}
                showHex={true}
                allowClear
                onChange={(value) => {
                  setColor(value);
                  debouncedSetColor(value);
                }}
              />
              {!color && (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {t('noColorSelected')}
                </Typography>
              )}
            </Box>
          )}
        </CardContent>
      </Card>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          '& .MuiPaper-root': {
            maxHeight: '200px',
            overflow: 'auto',
          },
        }}
      >
        {availableTimes.map((time) => (
          <Typography key={time} sx={{ p: 1 }}>
            {time}
          </Typography>
        ))}
      </Popover>
    </Box>
  );
};
